import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../../api/api.js'; // Use your authenticated API instance
import { API_BASE_URL } from '../../config';
import styles from './JobCreationAssistant.module.css';
import { Mic, X, Send, Bot, Loader2, Sparkles } from 'lucide-react';
import GoogleAddressAutocomplete from '../../components/Google/GoogleAddressAutocomplete'; // Updated import

//const JobCreationAssistant = ({ homeUserId }) => {
const JobCreationAssistant = ({ homeUserId, isOpen: externalIsOpen, onClose }) => {

    const ENABLE_VOICE = false; // Set to TRUE to re-enable voice 

    //  Ref for the text input
    const inputRef = useRef(null);

    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [voiceStatus, setVoiceStatus] = useState("idle"); // idle, listening, processing, speaking
    const messagesEndRef = useRef(null);

    // Flow State
    const [step, setStep] = useState("init"); 
    // Steps: init -> title -> profession_select -> profession_search -> location -> details -> budget -> timeline -> sitevisit -> summary -> confirm -> finished

    const [suggestedProfessions, setSuggestedProfessions] = useState([]);

    const [jobDraft, setJobDraft] = useState({
        title: "",
        professionId: null,
        professionName: "",
        location: "", 
        state: "", 
        postcode: "", 
        address: "", // Add Address
        description: "",
        budget: "",
        budgetAmount: null,
        timeline: "",
        siteVisit: "",
        jobMode: "Broadcast" // Broadcast or Invite
    });


    useEffect(() => {
        if (externalIsOpen) {
            setIsOpen(true);
            // Optional: If you want to start the flow immediately when opened via button:
            // if (messages.length === 0) startConversation(); 
        }
    }, [externalIsOpen]);

    // --- HELPER FOR CLOSING ---
    // Use this function on your "X" or "Close" button inside the chat UI
    const handleClose = () => {
        setIsOpen(false);
        if (onClose) onClose(); // Resets the Dashboard trigger
    };



    // Mutable ref for async access inside callbacks
    const conversationRef = useRef({ ...jobDraft });

    // --- SCROLL TO BOTTOM ---
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);




    // Force focus when opened or when step changes
    useEffect(() => {
        if (isOpen && step !== 'finished' && voiceStatus === 'idle') {
            // A small timeout ensures the CSS animation (slideUp) has started/finished
            // and the element is actually renderable before we grab focus.
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [isOpen, step, voiceStatus]);





    // --- 1. TTS & STT ---
    const speak = async (text, callback) => {

        if (!ENABLE_VOICE) {
            if (callback) callback();
            return;
        }

        setVoiceStatus("speaking");



        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/AiSearch/speak`, 
                { text: text }, 
                { responseType: 'blob' }
            );
            const audioUrl = URL.createObjectURL(response.data);
            const audio = new Audio(audioUrl);
            audio.play();
            audio.onended = () => {
                setVoiceStatus("idle");
                if (callback) callback();
            };
            audio.onerror = () => {
                setVoiceStatus("idle");
                if (callback) callback();
            };
        } catch (error) {
            console.error("TTS Error", error);
            setVoiceStatus("idle");
            if (callback) callback();
        }
    };

    const listen = () => {


        // MOD: Instant exit if voice is disabled
        if (!ENABLE_VOICE) return;

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return;

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-AU';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        setVoiceStatus("listening");
        recognition.start();

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            handleUserResponse(transcript);
        };

        recognition.onerror = () => {
            setVoiceStatus("idle");
        };
    };

    // --- HELPER: ADD MESSAGE ---
    const addBotMessage = (text, options = []) => {
        setMessages(prev => [...prev, { sender: 'bot', text, options }]);
        speak(text);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { sender: 'user', text }]);
    };

    // --- 2. CONVERSATION FLOW ENGINE ---

    const startConversation = () => {
        setIsOpen(true);
        if (messages.length === 0) {
            setStep("title");
            addBotMessage("Hi! I'm your AI Job Assistant. Briefly describe what you need to get done?");
        }
    };

    // --- CORE LOGIC ---
    const handleUserResponse = async (text) => {
        setVoiceStatus("processing");
        addUserMessage(text);
        
        // --- STEP 1: JOB TITLE CAPTURE ---
        if (step === "title") {
            // Save the raw text as the Job Title (e.g. "Fix my broken fence")
            conversationRef.current.title = text;
            
            // Now try to find the profession
            await searchAndSuggestProfession(text);
        }

        // --- STEP 1b: PROFESSION SELECTION ---
        else if (step === "profession_select") {
            // 1. Check if user clicked/said "None of these"
            if (text.toLowerCase().includes("none") || text.toLowerCase().includes("no")) {
                setStep("profession_search");
                addBotMessage("Okay, no problem. What is the name of the trade or profession you are looking for? (e.g. Plumber, Electrician)");
                setVoiceStatus("idle");
                return;
            }

            // 2. Check if text matches one of the suggestions (Name matching)
            const match = suggestedProfessions.find(p => p.name.toLowerCase() === text.toLowerCase());
            
            if (match) {
                // Exact match from options
                conversationRef.current.professionId = match.id;
                conversationRef.current.professionName = match.name;
                moveToLocationStep();
            } else {
                // User said something else, treat it as a new search term
                await searchAndSuggestProfession(text);
            }
        }

        // --- STEP 1c: MANUAL PROFESSION SEARCH ---
        else if (step === "profession_search") {
            await searchAndSuggestProfession(text);
        }

        // --- STEP 2: LOCATION ---
        else if (step === "location") {
            // Since we are using GoogleAddressAutocomplete, users should select from list. 
            // If they type manually and hit enter, it might be an issue if we strictly require selection.
            // But we can try to interpret their text input as an address if they didn't select.
            // However, the input field changes to autocomplete when step is 'location'.
            // If they typed manually, 'text' is what they typed.
            
            // Let's assume manual entry if not selected via dropdown
            conversationRef.current.address = text;
            // We might miss postcode/state/suburb here if they just typed "123 Main St" without selecting.
            // Ideally we force selection or use backend geocoding on the text.
            
            // Simple fallback: Ask for postcode if missing? Or just proceed.
            // Let's proceed and maybe AI extracts it.
            // Actually, let's keep the existing logic but adapted for address.
            
            setStep("details");
            addBotMessage(`Okay, address set to ${text}. Can you give me more details about the job?`);
        }

        // --- STEP 3: DETAILS ---
        else if (step === "details") {
            conversationRef.current.description = text;
            setStep("budget");
            addBotMessage("What is your budget? You can say an amount or select a range.", [
                "$500 - $1000", "$1000 - $2500", "$2500+", "Not Sure"
            ]);
        }

        // --- STEP 4: BUDGET ---
        else if (step === "budget") {
            conversationRef.current.budget = text;
            const numberPattern = /\d+/g;
            const matches = text.match(numberPattern);
            if (matches) conversationRef.current.budgetAmount = parseFloat(matches[0]);

            setStep("timeline");
            addBotMessage("When would you like this done by?", [
                "Urgently", "Within 1 week", "Within 2 weeks", "Flexible"
            ]);
        }

        // --- STEP 5: TIMELINE ---
        else if (step === "timeline") {
            conversationRef.current.timeline = text;
            setStep("sitevisit");
            addBotMessage("Is there a time for site visits?", [
                "By Appointment", "Anytime", "Weekends"
            ]);
        }

        // --- STEP 6: SITE VISIT & SUMMARY ---
        else if (step === "sitevisit") {
            conversationRef.current.siteVisit = text;
            setStep("job_mode");
            addBotMessage("Do you want to broadcast this job and have professionals contact you, or do you want to invite professionals yourself?", [
                "Broadcast (Professionals contact me)", 
                "Invite (I'll contact them)"
            ]);
        }

        // --- STEP 6b: JOB MODE ---
        else if (step === "job_mode") {
            const mode = text.toLowerCase().includes("invite") ? "Invite" : "Broadcast";
            conversationRef.current.jobMode = mode;
            
            setStep("confirm");
            
            const summary = `
**Summary:**
• Job: ${conversationRef.current.title}
• Professional: ${conversationRef.current.professionName}
• Location: ${conversationRef.current.address} 
• Budget: ${conversationRef.current.budget}
• Mode: ${mode}
            `;
            
            setMessages(prev => [...prev, { sender: 'bot', text: summary }]); 
            addBotMessage("Great! Ready to post? Say 'Yes' to confirm, or 'No' to save as draft.", ["Yes, Create Job", "No, Save Draft"]);
        }

        // --- STEP 7: CONFIRM & SUBMIT ---
        else if (step === "confirm") {
            const isYes = text.toLowerCase().includes("yes") || text.toLowerCase().includes("create");
            // If "Invite" mode, we set isPublish to false to avoid broadcasting to everyone
            // If "Broadcast" mode, isPublish is true.
            // If user says "No" (Save Draft), isPublish is false.
            
            let isPublish = false;
            if (isYes) {
                if (conversationRef.current.jobMode === "Broadcast") {
                    isPublish = true;
                } else {
                    // Invite mode - created as Open but not broadcasted automatically
                    isPublish = false; 
                }
            } else {
                // Save as draft
                isPublish = false;
            }

            try {
                const payload = {
                    userId: parseInt(homeUserId || 0),
                    name: conversationRef.current.title,
                    description: conversationRef.current.description,
                    profession: conversationRef.current.professionId 
                        ? [{ ProfessionId: conversationRef.current.professionId, name: conversationRef.current.professionName }] 
                        : [],
                    postcode: conversationRef.current.postcode || "", // Might be empty if just address used
                    mystate: conversationRef.current.state || "",
                    address: conversationRef.current.address || "", // Add Address
                    budget: conversationRef.current.budget,
                    budgetAmount: conversationRef.current.budgetAmount,
                    deadline: conversationRef.current.timeline,
                    siteVisit: conversationRef.current.siteVisit,
                    isPublish: isPublish,
                    isNew: true,
                    userType: 'HomeUser'
                };

                await api.post('/api/Jobs/createjob', payload);

                if (isYes) {
                    if (conversationRef.current.jobMode === "Invite") {
                        addBotMessage("Job created! You can now browse and invite professionals.");
                    } else {
                        addBotMessage("Job created successfully! Professionals have been notified.");
                    }
                } else {
                    addBotMessage("Job saved as a Draft. You can edit it in your dashboard.");
                }
                setStep("finished");

            } catch (err) {
                console.error("Create Job Error", err);
                addBotMessage("Sorry, there was an error creating the job. Please try again later.");
            }
        }
        
        setVoiceStatus("idle");
    };

    // --- HELPER: SEARCH PROFESSION ---
    const searchAndSuggestProfession = async (query) => {
        try {
            const res = await api.get(`/api/Jobs/AISmartSearch?search=${encodeURIComponent(query)}`);
            const matches = res.data || [];

            if (matches.length > 0) {
                setSuggestedProfessions(matches);
                // Create chips from the Top 5 results
                const options = matches.slice(0, 5).map(p => p.name);
                options.push("None of these");

                setStep("profession_select");
                addBotMessage(`I found these professionals for "${query}". Please select the best match:`, options);
            } else {
                // No matches
                setStep("profession_search");
                addBotMessage(`I couldn't find a profession matching "${query}". Could you try a different trade name? (e.g. Carpenter)`);
            }
        } catch (e) {
            setStep("profession_search");
            addBotMessage("I had trouble searching. What is the profession name?");
        }
    };

    const moveToLocationStep = () => {
        setStep("location");
        addBotMessage(`Got it, ${conversationRef.current.professionName}. What is the address for the job?`); // Changed text
    };

    // --- UI HELPERS ---
    const handleOptionClick = (optText) => {
        handleUserResponse(optText);
    };

    const handleSendClick = () => {
        if (!inputText.trim()) return;
        handleUserResponse(inputText);
        setInputText("");
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSendClick();
    };

    const handleLocationSelect = (locationData) => {
        // locationData = { fullAddress, postcode, suburb, state }
        conversationRef.current.postcode = locationData.postcode;
        conversationRef.current.location = locationData.suburb; // Keep using location for suburb logic if needed, or switch to address
        conversationRef.current.state = locationData.state;
        conversationRef.current.address = locationData.fullAddress; // Store address

        // Add user message for visual continuity
        addUserMessage(locationData.fullAddress);

        setStep("details");
        addBotMessage(`Okay, address set to ${locationData.fullAddress}. Can you give me more details about the job?`);
    };

    if (!isOpen) {
        return (
            <button className={styles.fab} onClick={startConversation} title="Job Assistant">
                <Sparkles className={styles.fabIcon} />
            </button>
        );
    }

    return (
        <div className={styles.chatWindow}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <Bot size={20} /> Job Assistant
                </div>
                {/* UPDATE THE ONCLICK HERE */}
                <button className={styles.closeBtn} onClick={handleClose}>
                    <X size={20} />
                </button>
            </div>

            {/* Messages */}
            <div className={styles.messageList}>
                {messages.map((msg, i) => (
                    <div key={i} className={`${styles.messageRow} ${msg.sender === 'user' ? styles.userRow : styles.botRow}`}>
                        <div className={`${styles.bubble} ${msg.sender === 'user' ? styles.userBubble : styles.botBubble}`}>
                            <div style={{whiteSpace: 'pre-wrap'}}>{msg.text}</div>
                            {msg.options && (
                                <div className={styles.optionsContainer}>
                                    {msg.options.map(opt => (
                                        <button key={opt} className={styles.optionChip} onClick={() => handleOptionClick(opt)}>
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {voiceStatus === 'processing' && (
                    <div className={styles.messageRow}>
                        <div className={`${styles.bubble} ${styles.botBubble}`}>
                            <Loader2 className={styles.spin} size={16} /> Thinking...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {step !== 'finished' && (
                <div className={styles.inputArea}
                
                    // FIX 1: Stop clicks from bubbling up to the Dashboard/Draggable container
                    onMouseDown={(e) => e.stopPropagation()} 
                    onTouchStart={(e) => e.stopPropagation()}
                
                >
                    {/*  Only show Mic button if enabled */}
                    {ENABLE_VOICE && (
                        <button 
                            className={`${styles.micBtn} ${voiceStatus === 'listening' ? styles.micBtnActive : ''} ${voiceStatus === 'processing' ? styles.micBtnProcessing : ''}`}
                            onClick={listen}
                            disabled={voiceStatus === 'processing' || voiceStatus === 'speaking'}
                        >
                            {voiceStatus === 'processing' ? <Loader2 className={styles.spin} size={20} /> : <Mic size={20} />}
                        </button>
                    )}
                    
                    {step === 'location' ? (
                        <div style={{flex: 1, marginRight: '8px'}}>
                            <GoogleAddressAutocomplete 
                                value={inputText}
                                onChange={(val) => setInputText(val)}
                                onSelect={handleLocationSelect} // Use onSelect for GoogleAddressAutocomplete
                                placeholder="Start typing address..."
                                //autoFocus
                            />
                        </div>
                    ) : (
                        <input 
                            ref={inputRef} 
                            //autoFocus
                            className={styles.input} 
                            placeholder="Type or speak..." 
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={voiceStatus !== 'idle'} 
                            style={{ cursor: 'text' }}
                        />
                    )}

                    <button className={styles.micBtn} onClick={handleSendClick} disabled={!inputText.trim()}>
                        <Send size={18} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default JobCreationAssistant;
